<script setup>
import { computed, defineProps } from "vue";
import { useFeatures } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatures";
import i18n from "@/core/plugins/vue-i18n";
import { stateMachineStates } from "@/components/ExternalApps/SalesChannelManagementApp/utility/stateMachineStates";
import { useStore } from "@/core/services/store";

const { selectedFeature } = useFeatures();
const store = useStore();

const props = defineProps({
  data: {
    type: Object,
    default: () => {}
  }
});

const salesOrderStatesArray = computed(
  () => stateMachineStates?.salesOrder?.map(state => state.value) ?? []
);

const badgeType = computed(() => {
  const {
    data: {
      item: { state }
    }
  } = props;

  if (
    selectedFeature.value === "tracking" &&
    salesOrderStatesArray.value.includes(state)
  ) {
    return "default";
  }

  const successStates = [
    "cancelled",
    "stateImported",
    "statePartiallyImported"
  ];

  if (
    (state === "imported" && selectedFeature.value !== "tracking") ||
    successStates.includes(state)
  ) {
    return "success";
  }

  if (state.toLowerCase().includes("error")) {
    return "error";
  }

  return "default";
});

const item = computed(() => {
  const { data } = props;
  const state = data?.item?.state ?? "";
  const entity = data?.field?.entity ?? "default";

  let translateString = JSON.parse(JSON.stringify(state));

  if (
    selectedFeature.value === "tracking" &&
    salesOrderStatesArray.value.includes(translateString)
  ) {
    translateString = "noStateUpdate";
  } else if (state.toLowerCase().includes("error")) {
    return (
      data?.item?.comment ?? i18n.t("salesChannelManagementApp.reporting.error")
    );
  }

  // For Amazon receipts the status label is driven purely by the destination
  // of the receipt (CONNECT-6105): imported into Xentral, imported to Connect
  // (intermediate steps shown in the detail modal) or exported to the sales
  // channel. Errors are already handled by the branch above.
  if (store.getters.appType === "amazon" && entity === "receipt") {
    const destination = String(data?.item?.destination ?? "").toLowerCase();

    if (destination === "xentral") {
      return i18n.t("salesChannelManagementApp.reporting.receiptStatus.xentral");
    }

    if (destination === "connect") {
      return i18n.t("salesChannelManagementApp.reporting.receiptStatus.connect");
    }

    return i18n.t("salesChannelManagementApp.reporting.receiptStatus.amazon");
  }

  return i18n.t(
    "salesChannelManagementApp.reporting.states." +
      entity +
      "." +
      translateString
  );
});
</script>

<template>
  <span class="state-badge" :class="'state-badge-' + badgeType">
    {{ item }}
  </span>
</template>

<style lang="scss" scoped>
.state-badge {
  padding: 6px 12px;
  border-radius: 6px;
  position: relative;
  white-space: nowrap;

  @each $type, $color in $connect-status-types {
    &-#{$type} {
      background-color: rgba($color, 0.3);
      color: darken($color, 30%);
    }
  }
}

// Dark mode: darkened text is unreadable on the translucent tint over a dark
// surface, so lighten the text instead.
[data-theme="dark"] .state-badge,
[data-theme="dark-colorblind"] .state-badge {
  @each $type, $color in $connect-status-types {
    &-#{$type} {
      background-color: rgba($color, 0.25);
      color: lighten($color, 20%);
    }
  }
}
</style>
